import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { massOps, type MassOp, type MassOpStatus, type MassOpTargetStatus } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  ChevronLeft, Pause, Play, Square, Bird, AlertTriangle,
  Power, RefreshCw, FileUp, Settings2,
} from 'lucide-react';

const STATUS_BADGE: Record<MassOpStatus, string> = {
  draft:     'badge',
  scheduled: 'badge-info',
  running:   'badge-brand',
  paused:    'badge-warn',
  completed: 'badge-up',
  aborted:   'badge-down',
};

const TARGET_STATUS_BADGE: Record<MassOpTargetStatus, string> = {
  pending: 'badge',
  queued:  'badge-info',
  running: 'badge-brand',
  success: 'badge-up',
  failed:  'badge-down',
  skipped: 'badge-warn',
};

const KIND_ICON = {
  firmware_upgrade: FileUp,
  set_params:       Settings2,
  reboot:           Power,
  apply_template:   RefreshCw,
} as const;

export default function MassOpDetail() {
  const { id } = useParams();
  const opId = Number(id);
  const qc = useQueryClient();
  const [tab, setTab] = useState<'targets' | 'runs' | 'settings'>('targets');

  const { data: op } = useQuery({
    queryKey: ['mass-op', opId],
    queryFn: () => massOps.show(opId),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && ['scheduled', 'running', 'paused'].includes(s) ? 5000 : false;
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['mass-op', opId] });
  const pause  = useMutation({ mutationFn: () => massOps.pause(opId),  onSuccess: invalidate });
  const resume = useMutation({ mutationFn: () => massOps.resume(opId), onSuccess: invalidate });
  const abort  = useMutation({ mutationFn: () => massOps.abort(opId),  onSuccess: invalidate });
  const arm    = useMutation({ mutationFn: () => massOps.arm(opId),    onSuccess: invalidate });

  if (!op) {
    return (
      <>
        <PageHeader title="Mass operation" />
        <div className="p-8 text-text-faint">Chargement…</div>
      </>
    );
  }

  const Icon = KIND_ICON[op.kind] ?? Power;
  const sched = `${new Date(op.start_at).toLocaleString()} → ${new Date(op.end_at).toLocaleString()}`;

  return (
    <>
      <PageHeader
        title={op.name}
        subtitle={
          <span className="flex items-center gap-2 text-xs">
            <span className={`badge ${STATUS_BADGE[op.status]}`}>{op.status}</span>
            {op.behind_schedule && (
              <span className="badge badge-warn" title="Behind schedule">
                <AlertTriangle size={9} />behind
              </span>
            )}
            <span className="flex items-center gap-1 text-text-muted">
              <Icon size={11} />{op.kind}
            </span>
          </span>
        }
        actions={
          <div className="flex items-center gap-1.5">
            <Link to="/app/mass-ops" className="btn btn-sm btn-ghost"><ChevronLeft size={11} />Liste</Link>
            {op.status === 'draft' && (
              <button onClick={() => { if (confirm('Armer cette opération ? Les targets seront matérialisés.')) arm.mutate(); }}
                disabled={arm.isPending} className="btn btn-sm btn-primary">
                <Play size={10} />Arm
              </button>
            )}
            {(op.status === 'running' || op.status === 'scheduled') && (
              <button onClick={() => pause.mutate()} disabled={pause.isPending} className="btn btn-sm">
                <Pause size={10} />Pause
              </button>
            )}
            {op.status === 'paused' && (
              <button onClick={() => resume.mutate()} disabled={resume.isPending} className="btn btn-sm btn-primary">
                <Play size={10} />Resume
              </button>
            )}
            {['scheduled', 'running', 'paused'].includes(op.status) && (
              <button onClick={() => { if (confirm('Abort cette opération ?')) abort.mutate(); }}
                disabled={abort.isPending} className="btn btn-sm text-danger">
                <Square size={10} />Abort
              </button>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Top cards */}
        <div className="grid grid-cols-4 gap-3">
          <div className="stat-tile">
            <div className="stat-label">Schedule</div>
            <div className="stat-value text-sm">{sched}</div>
            <div className="stat-sub">{op.daily_from_h}h–{op.daily_to_h}h • {op.timezone}</div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Concurrent cap</div>
            <div className="stat-value">{op.max_concurrent}</div>
            <div className="stat-sub">
              {op.concurrent ? `running: ${op.concurrent.current} / needed: ${op.concurrent.needed}` : '—'}
            </div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Progress</div>
            <div className="stat-value">{op.progress?.success_pct ?? 0}%</div>
            <div className="stat-sub">
              {op.progress ? `${op.progress.success}/${op.progress.total} success` : '—'}
            </div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Failures</div>
            <div className="stat-value">{op.progress?.failed ?? 0}</div>
            <div className="stat-sub">{op.progress?.skipped ?? 0} skipped</div>
          </div>
        </div>

        {/* Progress bar */}
        {op.progress && op.progress.total > 0 && (
          <div className="card card-pad">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Targets</span>
              <span className="text-xs font-mono">{op.progress.total} total</span>
            </div>
            <div className="h-3 bg-bg-2 rounded overflow-hidden flex">
              <Segment color="bg-success" pct={pct(op.progress.success, op.progress.total)} label={`${op.progress.success} success`} />
              <Segment color="bg-danger"  pct={pct(op.progress.failed,  op.progress.total)} label={`${op.progress.failed} failed`} />
              <Segment color="bg-brand"   pct={pct(op.progress.running, op.progress.total)} label={`${op.progress.running} running`} />
              <Segment color="bg-info"    pct={pct(op.progress.queued,  op.progress.total)} label={`${op.progress.queued} queued`} />
              <Segment color="bg-warning" pct={pct(op.progress.skipped, op.progress.total)} label={`${op.progress.skipped} skipped`} />
            </div>
            <div className="flex items-center gap-4 mt-2 text-[11px] text-text-muted flex-wrap">
              <Legend color="bg-success" label={`success ${op.progress.success}`} />
              <Legend color="bg-danger"  label={`failed ${op.progress.failed}`} />
              <Legend color="bg-brand"   label={`running ${op.progress.running}`} />
              <Legend color="bg-info"    label={`queued ${op.progress.queued}`} />
              <Legend color="bg-warning" label={`skipped ${op.progress.skipped}`} />
              <Legend color="bg-bg-2 border border-border" label={`pending ${op.progress.pending}`} />
            </div>
          </div>
        )}

        {/* Canary block */}
        {op.canary_count && (
          <CanaryBlock op={op} />
        )}

        {/* Tabs */}
        <div className="border-b border-border flex items-center gap-1">
          {(['targets', 'runs', 'settings'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm capitalize border-b-2 ${tab === t ? 'border-brand text-brand' : 'border-transparent text-text-muted hover:text-text'}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'targets'  && <TargetsTab opId={opId} />}
        {tab === 'runs'     && <RunsTab    opId={opId} />}
        {tab === 'settings' && <SettingsTab op={op} />}
      </div>
    </>
  );
}

function pct(n: number, total: number): number {
  return total > 0 ? (100 * n / total) : 0;
}

function Segment({ color, pct, label }: { color: string; pct: number; label: string }) {
  if (pct <= 0) return null;
  return <div className={`${color} h-full`} style={{ width: `${pct}%` }} title={label} />;
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-2 h-2 rounded ${color}`} />
      <span className="font-mono">{label}</span>
    </span>
  );
}

function CanaryBlock({ op }: { op: MassOp }) {
  const canaryCount = op.canary_count ?? 0;
  const canaryStatus = op.canary_status ?? 'pending';

  let verdict = '—';
  if (canaryStatus === 'in_progress' && op.canary_started_at && op.canary_wait_minutes) {
    const elapsedMin = Math.floor((Date.now() - new Date(op.canary_started_at).getTime()) / 60_000);
    const remaining = op.canary_wait_minutes - elapsedMin;
    verdict = remaining > 0 ? `verdict in ${remaining} min` : 'verdict pending';
  } else if (canaryStatus === 'passed')  verdict = 'canary passed';
  else if (canaryStatus === 'failed')    verdict = 'canary FAILED — op aborted';

  const colorClass = canaryStatus === 'passed' ? 'border-success/50 bg-success/5'
                   : canaryStatus === 'failed' ? 'border-danger/50 bg-danger/5'
                   : 'border-info/50 bg-info/5';

  return (
    <div className={`card card-pad ${colorClass}`}>
      <div className="flex items-center gap-2 mb-2">
        <Bird size={14} className="text-brand" />
        <h3 className="font-medium text-sm">Canary deployment</h3>
        <span className={`badge ${canaryStatus === 'passed' ? 'badge-up' : canaryStatus === 'failed' ? 'badge-down' : 'badge-info'}`}>
          {canaryStatus}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-3 text-xs">
        <div><span className="text-text-faint">Count:</span> <span className="font-mono">{canaryCount}</span></div>
        <div><span className="text-text-faint">Wait:</span> <span className="font-mono">{op.canary_wait_minutes}min</span></div>
        <div><span className="text-text-faint">Threshold:</span> <span className="font-mono">{op.canary_failure_threshold_pct}%</span></div>
        <div><span className="text-text-faint">Verdict:</span> <span className="font-mono">{verdict}</span></div>
      </div>
    </div>
  );
}

function TargetsTab({ opId }: { opId: number }) {
  const { data } = useQuery({
    queryKey: ['mass-op-targets', opId],
    queryFn: () => massOps.targets(opId, { per_page: 50 }),
    refetchInterval: 5000,
  });

  return (
    <div className="overflow-auto">
      <table className="luc-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Device</th>
            <th>MAC</th>
            <th>Firmware</th>
            <th>Attempts</th>
            <th>Canary</th>
            <th>Last error</th>
            <th>Finished</th>
          </tr>
        </thead>
        <tbody>
          {(data?.data ?? []).map((t) => (
            <tr key={t.id}>
              <td><span className={`badge ${TARGET_STATUS_BADGE[t.status] ?? 'badge'}`}>{t.status}</span></td>
              <td>
                <Link to={`/app/devices/${t.device_id}`} className="font-mono text-xs hover:text-brand">
                  {t.device?.serial_number ?? `#${t.device_id}`}
                </Link>
              </td>
              <td className="font-mono text-xs text-text-muted">{t.device?.mac_address ?? '—'}</td>
              <td className="font-mono text-xs text-text-muted">{t.device?.firmware_version ?? '—'}</td>
              <td className="text-xs">{t.attempts}</td>
              <td>{t.is_canary ? <Bird size={11} className="text-brand" /> : '—'}</td>
              <td className="text-xs text-danger font-mono truncate max-w-xs" title={t.last_error ?? ''}>
                {t.last_error ?? '—'}
              </td>
              <td className="text-xs font-mono text-text-faint">
                {t.finished_at ? new Date(t.finished_at).toLocaleString() : '—'}
              </td>
            </tr>
          ))}
          {!data?.data?.length && (
            <tr><td colSpan={8} className="text-center py-12 text-text-faint">Aucun target.</td></tr>
          )}
        </tbody>
      </table>
      {data && data.total > data.data.length && (
        <div className="text-xs text-text-faint p-3">
          {data.data.length} sur {data.total} (page {data.current_page}/{data.last_page})
        </div>
      )}
    </div>
  );
}

function RunsTab({ opId }: { opId: number }) {
  const { data } = useQuery({
    queryKey: ['mass-op-runs', opId],
    queryFn: () => massOps.runs(opId, { per_page: 100 }),
    refetchInterval: 5000,
  });

  return (
    <div className="overflow-auto">
      <table className="luc-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Target #</th>
            <th>Attempt</th>
            <th>Action</th>
            <th>Result</th>
            <th>Duration</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {(data?.data ?? []).map((r) => (
            <tr key={r.id}>
              <td className="text-xs font-mono text-text-muted">{new Date(r.created_at).toLocaleString()}</td>
              <td className="text-xs font-mono">{r.target_id}</td>
              <td className="text-xs">{r.attempt}</td>
              <td className="text-xs font-mono">{r.action}</td>
              <td>
                <span className={`badge ${r.result === 'success' ? 'badge-up' : r.result === 'failed' ? 'badge-down' : 'badge'}`}>
                  {r.result}
                </span>
              </td>
              <td className="text-xs font-mono text-text-muted">{r.duration_ms ? `${r.duration_ms}ms` : '—'}</td>
              <td className="text-xs text-danger truncate max-w-xs" title={r.error_message ?? ''}>
                {r.error_message ?? '—'}
              </td>
            </tr>
          ))}
          {!data?.data?.length && (
            <tr><td colSpan={7} className="text-center py-12 text-text-faint">Aucun run.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab({ op }: { op: MassOp }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="card card-pad">
        <h3 className="text-xs uppercase tracking-wider text-text-faint mb-3 font-medium">Filter</h3>
        <pre className="text-xs font-mono bg-bg-2 p-3 rounded overflow-auto">
          {JSON.stringify(op.filter_json, null, 2)}
        </pre>
      </div>
      <div className="card card-pad">
        <h3 className="text-xs uppercase tracking-wider text-text-faint mb-3 font-medium">Payload</h3>
        <pre className="text-xs font-mono bg-bg-2 p-3 rounded overflow-auto">
          {JSON.stringify(op.payload_json, null, 2)}
        </pre>
      </div>
      <div className="card card-pad col-span-2">
        <h3 className="text-xs uppercase tracking-wider text-text-faint mb-3 font-medium">Schedule & limits</h3>
        <dl className="text-xs grid grid-cols-2 gap-x-6 gap-y-1.5 font-mono">
          <Row k="start_at"         v={new Date(op.start_at).toLocaleString()} />
          <Row k="end_at"           v={new Date(op.end_at).toLocaleString()} />
          <Row k="daily_window"     v={`${op.daily_from_h}h - ${op.daily_to_h}h`} />
          <Row k="timezone"         v={op.timezone} />
          <Row k="max_concurrent"   v={String(op.max_concurrent)} />
          <Row k="exclude_weekends" v={op.exclude_weekends ? 'yes' : 'no'} />
          <Row k="canary_count"            v={op.canary_count != null ? String(op.canary_count) : '—'} />
          <Row k="canary_wait_minutes"     v={op.canary_wait_minutes != null ? `${op.canary_wait_minutes}min` : '—'} />
          <Row k="canary_threshold_pct"    v={op.canary_failure_threshold_pct != null ? `${op.canary_failure_threshold_pct}%` : '—'} />
          <Row k="canary_status"           v={op.canary_status ?? '—'} />
        </dl>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt className="text-text-faint">{k}</dt>
      <dd>{v}</dd>
    </>
  );
}
